/**
 * Champ code promo sur la page panier.
 * Dépend exclusivement de window.Shopify.actions (Actions standard Shopify).
 * Si l'API n'est pas disponible, le bloc reste masqué.
 */

// --- Types ---

interface ShopifyDiscountCode {
  code: string;
  applicable?: boolean;
}

interface ShopifyCart {
  id: string;
  discountCodes: Array<ShopifyDiscountCode | string>;
}

interface GetCartResponse {
  cart: ShopifyCart;
}

interface ShopifyActions {
  getCart: () => Promise<GetCartResponse>;
  updateCart: (payload: { discountCodes: string[] }) => Promise<unknown>;
  openCart: () => void;
}

interface ShopifyGlobal {
  actions: ShopifyActions;
}

declare global {
  interface Window {
    Shopify?: ShopifyGlobal;
  }
}

// --- Classe ---

class CartDiscountField {
  private readonly root: HTMLElement;
  private readonly actions: ShopifyActions;

  private readonly input: HTMLInputElement;
  private readonly applyBtn: HTMLButtonElement;
  private readonly activeBox: HTMLElement | null;
  private readonly currentLabel: HTMLElement | null;
  private readonly removeBtn: HTMLButtonElement | null;

  /**
   * Instancie le champ si toutes les conditions sont réunies :
   * - window.Shopify.actions est disponible
   * - le conteneur [data-cart-discount] existe dans le DOM
   * - les éléments requis (input, bouton appliquer) sont présents
   *
   * Retourne null sinon.
   */
  static create(): CartDiscountField | null {
    const actions = CartDiscountField.resolveActions();
    if (!actions) return null;
    const root = document.querySelector<HTMLElement>("[data-cart-discount]");
    if (!root) return null;
    return new CartDiscountField(root, actions);
  }

  private static resolveActions(): ShopifyActions | null {
    const shopify = window.Shopify;

    if (shopify?.actions) {
      return shopify.actions;
    }
    return null;
  }

  private constructor(root: HTMLElement, actions: ShopifyActions) {
    this.root = root;
    this.actions = actions;

    const input = root.querySelector<HTMLInputElement>("#CartDiscountCode");
    const applyBtn = root.querySelector<HTMLButtonElement>(
      "[data-cart-discount-apply]",
    );
    if (!input || !applyBtn) {
      throw new Error("[cart-discount] Éléments requis manquants");
    }

    this.input = input;
    this.applyBtn = applyBtn;
    this.activeBox = root.querySelector<HTMLElement>(
      "[data-cart-discount-active]",
    );
    this.currentLabel = root.querySelector<HTMLElement>(
      "[data-cart-discount-current]",
    );
    this.removeBtn = root.querySelector<HTMLButtonElement>(
      "[data-cart-discount-remove]",
    );
  }

  /** Point d'entrée : attache les listeners et synchronise l'UI initiale. */
  mount(): void {
    this.root.hidden = false;
    this.applyBtn.addEventListener("click", () => void this.apply());
    this.removeBtn?.addEventListener("click", () => void this.remove());
    this.input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        void this.apply();
      }
    });
    void this.refresh();
  }

  // --- Actions ---

  /** Applique le code saisi (en préservant les codes déjà présents). */
  private async apply(): Promise<void> {
    const code = this.input.value.trim().toUpperCase();
    if (!code) {
      this.input.focus();
      return;
    }

    this.applyBtn.disabled = true;
    try {
      const existing = await this.fetchCodes();
      if (existing.includes(code)) {
        this.applyBtn.disabled = false;
        return;
      }
      // le merge ne fonctionne pas correctement.
      // const codes = [...existing, code];
      const codes = [code];
      await this.actions.updateCart({ discountCodes: codes });
      //window.location.reload();
    } catch (error) {
      console.error("[cart-discount] Erreur application:", error);
      this.applyBtn.disabled = false;
    }
  }

  /** Supprime tous les codes promo du panier. */
  private async remove(): Promise<void> {
    if (!this.removeBtn) return;
    this.removeBtn.disabled = true;
    try {
      await this.actions.updateCart({ discountCodes: [] });
      // window.location.reload();
    } catch (error) {
      console.error("[cart-discount] Erreur suppression:", error);
      this.removeBtn.disabled = false;
    }
  }

  // --- État / rendu ---

  /** Lit les codes actuellement appliqués sur le panier. */
  private async fetchCodes(): Promise<string[]> {
    const res = await this.actions.getCart();
    return CartDiscountField.normalize(res?.cart?.discountCodes);
  }

  /** Synchronise l'UI avec l'état réel du panier. */
  private async refresh(): Promise<void> {
    try {
      const codes = await this.fetchCodes();
      this.render(codes);
    } catch (error) {
      console.error("[cart-discount] Erreur lecture:", error);
    }
  }

  private render(codes: string[]): void {
    const hasCodes = codes.length > 0;
    if (this.activeBox) this.activeBox.hidden = !hasCodes;
    if (this.currentLabel)
      this.currentLabel.textContent = hasCodes ? codes.join(", ") : "";
    this.input.value = hasCodes ? codes[0] : "";
  }

  /** Normalise la liste hétérogène renvoyée par l'API Storefront. */
  private static normalize(
    codes: Array<ShopifyDiscountCode | string> | undefined,
  ): string[] {
    if (!Array.isArray(codes)) return [];
    return codes
      .map((dc) => (typeof dc === "string" ? dc : dc?.code))
      .filter(
        (code): code is string => typeof code === "string" && code.length > 0,
      );
  }
}

// --- Bootstrap ---
document.addEventListener("DOMContentLoaded", () => {
  CartDiscountField.create()?.mount();
});
